import { type QConfigStructureData } from '../types';

/**
 * Lexicographically compares two order paths by walking indices in lockstep until they differ.
 * If one path is a prefix of the other (rare — implies different tree depths), the
 * shorter one sorts first. This is what makes the full-hierarchy ordering work.
 * @param a - First order path array
 * @param b - Second order path array
 * @returns A negative number if a < b, positive if a > b, or 0 if equal
 */
function comparePaths(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return a.length - b.length;
}

/**
 * Builds the order path for a learning objective by traversing up the parent hierarchy.
 * @param losId - The ID of the learning objective (refId) to find the order path for
 * @param structureById - Map of structures indexed by their ID for parent lookup
 * @param structureByRefId - Map of structures indexed by their refId to locate the starting structure
 * @returns An array of order indices representing the hierarchy path from root to the structure
 */
function getOrderPath(
  losId: string,
  structureById: Map<string, QConfigStructureData>,
  structureByRefId: Map<string, QConfigStructureData>,
): number[] {
  const path: number[] = [];
  let current = structureByRefId.get(losId);
  while (current) {
    path.unshift(current.order);
    current = current.parentId ? structureById.get(current.parentId) : undefined;
  }
  return path;
}

export function sortLosByStructure(
  disabledLos: { id: string; name: string | null }[],
  qConfigStructures: QConfigStructureData[],
) {
  if (disabledLos.length === 0) {
    return [];
  }
  // 1. Build lookup maps for structures by ID and refId
  const structureById = new Map(qConfigStructures.map((s) => [s.id, s]));

  // 2. Build lookup map for LOS structures by refId (disabledLos.id === structure.refId)
  const structureByRefId = new Map(
    qConfigStructures.filter((s) => !!s.refId).map((s) => [s.refId as string, s]),
  );

  // 3. Compute order paths for each disabled LOS and sort by those paths
  const paths = disabledLos.map((los) => getOrderPath(los.id, structureById, structureByRefId));

  // 4. Sort indices of disabledLos based on their corresponding paths
  const indices = disabledLos.map((_, i) => i);
  indices.sort((a, b) => comparePaths(paths[a], paths[b]));

  // 5. Map sorted indices back to disabled LOS objects
  return indices.map((i) => ({ id: disabledLos[i].id, name: disabledLos[i].name }));
}
